import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { MoreVertical, RefreshCw, GripVertical, Maximize2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { TableWidget } from "./table-widget"
import { ChartWidget } from "./chart-widget"
import { NumberWidget } from "./number-widget"
import { TextWidget } from "./text-widget"
import { useWidgetData, useResyncWidget, useDeleteWidget, useUpdateWidget } from "@/hooks/useBoard"
import { useToast } from "@/hooks/useToast"
import { ROUTES } from "@/routes/path"
import type { BoardWidget } from "@/lib/http/board"
import Loader from "@/components/ui/loader"

interface WidgetProps {
  widget: BoardWidget
  boardId: string
  data?: any
  onDelete?: () => void
}

export function Widget({ widget, boardId, data: initialData, onDelete }: WidgetProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: cachedData, isLoading: isLoadingData } = useWidgetData(boardId, widget.id)
  const resyncMutation = useResyncWidget()
  const deleteMutation = useDeleteWidget()
  const updateWidgetMutation = useUpdateWidget()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isRenameOpen, setIsRenameOpen] = useState(false)
  const [renameValue, setRenameValue] = useState(widget.name)

  // Use cached data, initialData, or empty
  const widgetData = cachedData || initialData || undefined
  const isLoading = isLoadingData || resyncMutation.isPending

  const handleResync = async () => {
    try {
      await resyncMutation.mutateAsync({ boardId, widgetId: widget.id })
      toast({
        description: "Widget data refreshed successfully",
      })
    } catch (error: any) {
      toast({
        description: error?.response?.data?.error || "Failed to refresh widget data",
        variant: "destructive",
      })
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ boardId, widgetId: widget.id })
      setIsDeleteOpen(false)
      toast({
        description: "Widget deleted successfully",
      })
      // Call the onDelete callback if provided (for parent component cleanup)
      if (onDelete) {
        onDelete()
      }
    } catch (error: any) {
      toast({
        description: error?.response?.data?.error || "Failed to delete widget",
        variant: "destructive",
      })
    }
  }

  const handleRename = async () => {
    if (!renameValue.trim()) {
      toast({
        description: "Widget name is required",
        variant: "destructive",
      })
      return
    }

    try {
      await updateWidgetMutation.mutateAsync({
        boardId,
        widgetId: widget.id,
        name: renameValue.trim(),
      })
      setIsRenameOpen(false)
      toast({
        description: "Widget renamed successfully",
      })
    } catch (error: any) {
      toast({
        description: error?.response?.data?.error || "Failed to rename widget",
        variant: "destructive",
      })
    }
  }

  // Update rename value when widget name changes
  useEffect(() => {
    setRenameValue(widget.name)
  }, [widget.name])

  const handleEdit = () => {
    // Navigate to query editor with widget data (include boardId/widgetId so editor can Save back)
    navigate(ROUTES.DASHBOARD.EDITOR.QUERY_EDITOR, {
      state: {
        widget: {
          board_id: boardId,
          widget_id: widget.id,
          name: widget.name,
          sql_query: widget.sql_query,
          resource_id: widget.resource_id,
          widget_type: widget.widget_type,
          config: widget.config,
          data: widgetData, // Include current widget data for immediate display
        },
      },
    })
  }

  const handleFullscreen = () => {
    navigate(ROUTES.DASHBOARD.BOARDS.WIDGET(boardId, widget.id))
  }

  return (
    <div
      className="group relative flex h-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all hover:border-gray-300 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
      style={{ userSelect: "none" }}
    >
      <div className="flex h-full w-full flex-col" style={{ userSelect: "text" }}>
        {/* Compact Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gray-50/50 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/50">
          <div className="drag-handle flex cursor-move items-center gap-1.5">
            <GripVertical className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            <h3 className="truncate text-xs font-semibold text-gray-900 dark:text-white">{widget.name}</h3>
          </div>
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleFullscreen} tooltip="Full screen">
              <Maximize2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleResync}
              disabled={isLoading}
              tooltip="Resync data"
            >
              <RefreshCw className={`h-3 w-3 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="text-xs">
                <DropdownMenuItem onClick={() => setIsRenameOpen(true)} className="text-xs">
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEdit} className="text-xs">
                  Edit
                </DropdownMenuItem>
                {onDelete && (
                  <DropdownMenuItem
                    onClick={() => setIsDeleteOpen(true)}
                    className="text-xs text-red-600 dark:text-red-400"
                  >
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Widget Content - Scrollable */}
        <div className="min-h-0 flex-1 overflow-auto p-3">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader className="h-5 w-5" />
            </div>
          ) : (
            <>
              {widget.widget_type === "table" && <TableWidget data={widgetData} config={widget.config} />}
              {widget.widget_type === "chart" && <ChartWidget data={widgetData} config={widget.config} />}
              {widget.widget_type === "number" && <NumberWidget data={widgetData} config={widget.config} />}
              {widget.widget_type === "text" && <TextWidget data={widgetData} config={widget.config} />}
            </>
          )}
        </div>
      </div>

      {/* Rename Dialog */}
      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Widget</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rename-widget-name">Name</Label>
              <Input
                id="rename-widget-name"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Widget name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleRename()
                  }
                }}
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenameOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={updateWidgetMutation.isPending || !renameValue.trim()}>
              {updateWidgetMutation.isPending ? (
                <span className="mr-1.5 h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Widget</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete &ldquo;{widget.name}&rdquo;? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
